// Strings and date formatting. nb (Norwegian bokmål) is the source and
// defines the keys; en is below it, the other languages are in lang/ (each
// typed Record<Key, Text>, so a missing or extra key doesn't compile).
// t() returns plain text: escape it before putting it in HTML. A value can be
// { one, other }, picked by vars.n (see PLURAL_ONE).

import { toTime, hhmm, parseKey, weekday } from '../shared/model.ts';
import type { TimeWindow } from '../shared/model.ts';
import { da } from './lang/da.ts';
import { is } from './lang/is.ts';
import { nn } from './lang/nn.ts';
import { sv } from './lang/sv.ts';

export type Text = string | { one: string; other: string };

const nb = {
  appName: 'Finn tid',
  tagline: 'finn dagen som passer for flest',
  privacy: 'Personvern',
  sourceCode: 'Kildekode',
  heroTitle: 'Finn tiden som passer for flest',
  heroLead: 'Lag en avstemning, velg mulige dager eller klokkeslett og del lenken. Alle markerer når de kan – uten innlogging.',
  newEvent: 'Ny avstemning',
  yourEvents: 'Dine avstemninger',
  yourEventsHint: 'Huskes bare på denne enheten. Lenkene virker på alle enheter.',
  showAllEvents: 'Vis alle ({n})',
  findLink: 'Finn avstemningene dine fra en annen enhet',
  findTitle: 'Finn avstemningene dine',
  findLead: 'Skriv e-postadressen du oppga da du laget eller svarte på en avstemning. Vi sender en kode til den, og så legges avstemningene til på denne enheten.',
  findEmail: 'E-postadresse',
  findSend: 'Send kode',
  findSent: 'Er adressen brukt på en avstemning her, kommer det straks en e-post med en kode. Koden virker i 10 minutter.',
  findCode: 'Kode fra e-posten',
  findShow: 'Vis avstemningene mine',
  findLinkLead: 'Trykk på knappen for å legge avstemningene for e-postadressen din til på denne enheten. Lenken virker én gang.',
  findAgain: 'Send ny kode',
  findOther: 'Bruk en annen adresse',
  findAsked: 'Ba om en ny kode.',
  findFound: { one: 'Fant {n} avstemning. Den er lagt til på denne enheten.', other: 'Fant {n} avstemninger. De er lagt til på denne enheten.' },
  findNone: 'Fant ingen avstemninger med denne adressen.',
  openAdmin: 'Admin',
  openMine: 'Mine svar',
  open: 'Åpne',
  tagAdmin: 'Admin',
  tagAnswered: 'Svart',
  forget: 'Glem på denne enheten',
  leave: 'Logg ut',
  leaveQ: 'Logge ut på denne enheten?',
  leaveTextAdmin: 'Admin-lenken fjernes fra denne enheten, og avstemningen forsvinner fra listen din her. For å komme tilbake trenger du lenken, så ta vare på den først.',
  leaveTextMine: 'Den personlige lenken din fjernes fra denne enheten, og avstemningen forsvinner fra listen din her. For å endre svarene senere trenger du lenken, så ta vare på den først.',
  leaveEmail: 'Du kan også få lenken tilbake med en kode til e-postadressen din.',
  leftToast: 'Logget ut på denne enheten',
  step1: 'Velg mulige dager',
  step1Text: 'Datoer, eller klokkeslett på hver dag. Fjern helger og helligdager med ett trykk.',
  step2: 'Del lenken',
  step2Text: 'Én lenke til alle. Hver deltaker får sin egen lenke for å endre svarene.',
  step3: 'Se hva som passer',
  step3Text: 'Dagene og tidene som passer for flest, kommer øverst.',
  loading: 'Laster …',
  pageNotFound: 'Fant ikke siden',
  toHome: 'Til forsiden',
  eventNotFound: 'Fant ikke avstemningen',
  eventNotFoundText: 'Den kan være slettet, eller lenken er feil. Avstemninger slettes automatisk 30 dager etter siste dato.',
  retry: 'Prøv igjen',
  reload: 'Last inn på nytt',
  newVersion: 'En ny versjon er klar.',
  changedMeanwhile: 'Noen andre endret nettopp avstemningen.',
  back: 'Tilbake',
  cancel: 'Avbryt',
  ok: 'OK',
  done: 'Ferdig',
  save: 'Lagre',
  saved: 'Lagret',
  delete: 'Slett',
  deleted: 'Slettet',
  copy: 'Kopier',
  copied: 'Kopiert',
  share: 'Del',
  link: 'Lenke',
  more: 'Mer',
  yes: 'Ja',
  maybe: 'Kanskje',
  no: 'Nei',
  name: 'Navn',
  sum: 'Kan',
  you: 'deg',
  language: 'Språk',

  editEvent: 'Endre avstemningen',
  aboutEvent: 'Om avstemningen',
  eventName: 'Navn på avstemningen',
  eventNamePh: 'For eksempel «Julebord»',
  descriptionOptional: 'Beskrivelse (valgfritt)',
  locationOptional: 'Sted (valgfritt)',
  whatToFind: 'Hva skal dere finne?',
  'mode.dates': 'Dager',
  'mode.datesHint': 'Hvilke dager passer?',
  'mode.times': 'Klokkeslett',
  'mode.timesHint': 'Hvilke tider passer, dag for dag?',
  fromHour: 'Fra kl.',
  toHour: 'Til kl.',
  slotLength: 'Ruter på',
  meetingLength: 'Møtet varer',
  timeZone: 'Tidssone',
  timeZoneHint: 'Alle ser tidene i denne tidssonen.',
  possibleDays: 'Mulige dager',
  theme: 'Tema',
  'theme.auto': 'Automatisk',
  'theme.autoHint': 'Lys eller mørk, som enheten',
  'theme.light': 'Lys',
  'theme.dark': 'Mørk',
  'theme.hacker': 'Hacker',
  rangeHint: 'Dra fra én dag til en annen for å ta med alle dagene mellom, eller klikk den første og shift-klikk den siste.',
  rangeHintTouch: 'Hold fingeren på en dag og dra til en annen for å ta med alle dagene mellom.',
  possibleDaysHint: 'Trykk på dagene som kan passe, eller velg flere med knappene. Knappene endrer bare utvalget – du kan fortsatt legge til eller fjerne enkeltdager etterpå.',
  period2w: 'Neste 2 uker',
  period4w: 'Neste 4 uker',
  periodMonth: 'Hele {month}',
  periodHint: 'Velger akkurat disse dagene (i stedet for dem du har valgt)',
  moreMonths: 'Vis neste måned',
  selNone: 'Ingen',
  undo: 'Angre',
  redo: 'Gjør om',
  removedDays: { one: 'Fjernet 1 dag', other: 'Fjernet {n} dager' },
  timesCleared: 'Tidene er fjernet',
  weekdays: 'Hverdager',
  weekends: 'Helger',
  holidays: 'Helligdager',
  weekdaysHint: 'Fjern de valgte hverdagene (mandag–fredag), eller velg alle i kalenderen',
  weekendsHint: 'Fjern de valgte lørdagene og søndagene, eller velg alle i kalenderen',
  holidaysHint: 'Fjern de valgte helligdagene, eller velg alle helligdagene i kalenderen',
  weekdayHint: 'Fjern de valgte {day}ene, eller velg alle i kalenderen',
  toggleWeek: 'Velg eller fjern uke {n}',
  weekShort: 'U',
  nDays: { one: '1 dag valgt', other: '{n} dager valgt' },
  nSlots: { one: '1 tidsrute', other: '{n} tidsruter' },
  nDaysNoTimes: { one: '1 dag uten tider', other: '{n} dager uten tider' },
  possibleTimes: 'Mulige tider',
  possibleTimesHint: 'Dra over rutene for å markere eller fjerne tider. Trykk på en dag øverst for flere valg.',
  possibleTimesHintTouch: 'Trykk på en rute for å markere eller fjerne den, eller hold fingeren på en rute og dra over flere. Trykk på en dag øverst for flere valg.',
  'preset.all': 'Hele dagen',
  'preset.work': 'Arbeidstid 9–17',
  'preset.evening': 'Kveld 17–22',
  'preset.clear': 'Ingen tider',
  pickDaysFirst: 'Velg dager i kalenderen først.',
  dayAll: 'Velg hele dagen',
  dayClear: 'Fjern alle tider denne dagen',
  dayCopy: 'Bruk disse tidene på alle dager',
  dayCopyHint: 'Dager du legger til senere, får også disse tidene',
  dayRemove: 'Fjern dagen',
  settings: 'Innstillinger',
  'set.maybe': 'Tillat «kanskje»',
  'set.maybeHint': 'Deltakerne kan svare «kan hvis det må til».',
  'set.maybeCounts': '«Kanskje» teller som at de kan',
  'set.maybeCountsHint': 'Rangeringen teller alle som kan eller kanskje kan. Ellers teller bare ja, og ved likt antall ja står den med flest «kanskje» først.',
  'set.openEdit': 'endre og slette alles svar',
  'set.openEditHint': 'Ellers bare sitt eget.',
  'set.hidden': 'se svarene',
  'set.hiddenHint': 'Hvem som har svart hva, og hvor mange. Ellers ser bare arrangørene det, og hver deltaker sitt eget svar.',
  'set.closed': 'Stengt for nye svar',
  'set.closedHint': 'Ingen andre enn arrangørene kan svare eller endre noe.',
  'set.newNames': 'svare under et nytt navn',
  'set.newNamesHint': 'Ellers kan bare navnene dere har satt på listen, svare.',
  'set.claims': 'svare som et navn på listen',
  'set.claimsHint': 'Ved å trykke på navnet sitt med avstemningslenken. Ellers trenger de den personlige lenken sin fra dere.',
  'set.rename': 'endre navnet sitt',
  'set.renameHint': 'Ellers står navnet slik det ble skrevet først, eller slik dere skrev det på listen.',
  'set.decide': 'velge hva det blir',
  'set.decideHint': 'Velge den endelige dagen eller tiden, og angre valget. Avstemningen forblir åpen: bare arrangørene stenger den.',
  'set.details': 'endre navn, sted og beskrivelse',
  'set.detailsHint': 'På avstemningen. Det vises ikke hvem som endret dem, men dere kan alltid endre dem tilbake.',
  yourEmailOptional: 'Din e-post (valgfritt)',
  adminEmailsOptional: 'E-post til arrangørene (valgfritt)',
  addDetails: 'Legg til beskrivelse og sted',
  manyWeeks: 'Deltakerne må gå gjennom {n} uker med tider, én uke om gangen på mobil. Færre dager gjør det raskere å svare.',
  emailOptional: 'E-post (valgfritt)',
  addEmail: 'Legg til e-post',
  adminEmailHint: 'Lagres med avstemningen, så du kan finne avstemningen igjen på en annen enhet (vi sender deg en kode). Er dere flere arrangører, kan dere legge inn en e-post hver. Bare de med admin-lenken ser dem.',
  addEmailRow: 'Legg til en e-post til',
  removeEmail: 'Fjern e-posten',
  createEvent: 'Lag avstemning',
  saveChanges: 'Lagre endringer',
  lostAnswers: {
    one: '1 svar gjelder dager eller tider som fjernes, og blir slettet.',
    other: '{n} svar gjelder dager eller tider som fjernes, og blir slettet.',
  },
  saveAnyway: 'Lagre likevel',

  createdTitle: 'Avstemningen er klar!',
  createdText: 'Del avstemningslenken med dem du vil invitere. Ta vare på admin-lenken: med den endrer du avstemningen, og du kan dele den med andre arrangører.',
  savedNewTitle: 'Takk, {name}! Svarene er lagret.',
  savedNewText: 'Dette er din personlige lenke. Med den kan du endre svarene senere, fra hvilken som helst enhet.',
  savedNewHint: 'Ta vare på lenken: legg den til som bokmerke eller send den til deg selv. Ikke del den – den som har lenken, kan endre svarene dine.',
  yourLink: 'Din personlige lenke',
  linkInvalid: 'Denne lenken virker ikke lenger. Den kan være byttet ut med en ny, eller svaret er slettet. Du ser avstemningen som gjest.',
  storedAdmin: 'Du er admin for denne avstemningen på denne enheten.',
  storedMine: 'Denne enheten har den personlige lenken din til denne avstemningen.',
  closedInfo: 'Avstemningen er stengt for nye svar.',
  closedAdmin: 'Avstemningen er stengt. Bare arrangørene kan legge til eller endre svar.',
  metaDates: { one: '1 mulig dag', other: '{n} mulige dager' },
  metaTimes: 'Klokkeslett · møtet varer {dur}',
  timesIn: 'Tider i {tz}',
  yourTz: '(du er i {tz})',
  metaHidden: 'Bare arrangørene ser svarene',
  metaOpenEdit: 'Alle kan endre svarene',
  permsTitle: 'Deltakerne kan',
  metaListOnly: 'Bare navnene på listen kan svare',
  metaDecide: 'Alle kan velge hva det blir',
  editDetails: 'Endre navn og sted',
  editDetailsHint: 'Alle med avstemningslenken ser endringen.',
  detailsSaved: 'Endringene er lagret.',
  listOnlyInfo: 'Bare de på listen kan svare, med den personlige lenken de har fått fra arrangørene.',
  listOnlyClaim: 'Bare navnene på listen kan svare.',
  nameFixed: 'Navnet kan ikke endres i denne avstemningen.',
  youAreAdmin: 'Du er admin',
  adminHint: 'Du kan endre avstemningen og alle svarene.',
  addAnswers: 'Legg til svar',
  personName: 'Navn',
  close: 'Steng',
  reopen: 'Åpne igjen',
  hiName: 'Hei, {name}!',
  personalHint: 'Dette er din personlige lenke. Bruk den når du vil endre svarene.',
  editYourAnswers: 'Endre svarene dine',
  copyMyLink: 'Kopier lenken min',
  shareEvent: 'Del avstemningen',
  addYourAnswers: 'Legg til dine svar',
  shareTitle: 'Del avstemningen',
  shareHint: 'Send denne lenken til alle som skal være med.',
  eventLink: 'Avstemningslenke',
  adminLinkTitle: 'Admin-lenken',
  adminLinkHint: 'Del den med dem som skal være arrangører sammen med deg. Alle som har den, kan endre avstemningen og alle svarene.',
  qrTitle: 'QR-kode for avstemningen',
  resultsTitle: 'Hva passer best?',
  noAnswersYet: 'Ingen har svart ennå.',
  noneAvailable: 'Ingen av alternativene passer for noen ennå.',
  hiddenInfo: 'Svarene er skjult: bare arrangørene ser dem, og hvor mange som har svart.',
  'tab.cal': 'Kalender',
  'tab.list': 'Liste',
  'tab.table': 'Tabell',
  'tab.grid': 'Rutenett',
  legendFew: 'Få kan',
  legendMany: 'Mange kan',
  legendTop: 'Flest kan',
  tapForDetails: 'Trykk på et alternativ for å se hvem som kan.',
  tapCellForDetails: 'Trykk på en rute for å se hvem som kan.',
  nOfM: '{n} av {m} kan',
  decidedTitle: 'Avtalt',
  decidedTag: 'Valgt',
  decidedThis: 'Dette er valgt',
  decideThis: 'Velg {when}',
  decideQ: 'Velge {when}? Alle som åpner avstemningen ser valget, og avstemningen stenges for nye svar.',
  decideOk: 'Velg',
  decidedToast: 'Valgt. Del lenken, så ser alle det.',
  undecideQ: 'Angre valget? Avstemningen forblir stengt til du åpner den igjen.',
  decideQOpen: 'Velge {when}? Alle som åpner avstemningen ser valget.',
  undecideQOpen: 'Angre valget?',
  undoDecision: 'Angre valget',
  undecidedToast: 'Valget er angret',
  addToCalendar: 'Legg i kalender',
  nMaybe: { one: '1 kanskje', other: '{n} kanskje' },
  nMaybeIncl: { one: 'inkl. 1 kanskje', other: 'inkl. {n} kanskje' },
  moreTied: { one: 'Vis 1 til med like mange som kan', other: 'Vis {n} til med like mange som kan' },
  showFewer: 'Vis færre',
  rankYesFirst: 'Rangert etter flest ja. Ved likt antall står den med flest «kanskje» først.',
  rankMaybeCounts: 'Rangert etter flest som kan, «kanskje» medregnet. Ved likt antall står den med flest ja først.',
  canCome: 'Kan',
  ifNeedBe: 'Kanskje',
  cannot: 'Kan ikke',
  responses: 'Svar',
  hiddenPeople: 'Svarene er skjult. Du ser bare ditt eget.',
  sumYes: 'Kan {n} av {m}',
  sumYesTime: 'Kan {dur}',
  maybeDur: 'kanskje {dur}',
  sumNoneDates: 'Kan ingen av dagene',
  sumNoneTimes: 'Kan ingen av tidene',
  nOfTotal: '{n} av {m}',
  answeredOf: '{n} av {m} har svart',
  notAnswered: 'Har ikke svart ennå',
  noAnswer: 'Ikke svart',
  youNotAnswered: 'Du har ikke svart ennå.',
  addNames: 'Legg til navn',
  addNamesTitle: 'Hvem er invitert?',
  addNamesText: 'Ett navn per linje, eller lim inn en liste. Da ser du hvem som ikke har svart ennå, og hver får sin egen lenke som du kan sende dem. Navnene vises for alle som åpner avstemningen, med mindre svarene er skjult.',
  addNamesPh: 'Kari\nOla\nPer',
  addNamesOk: 'Legg til',
  namesAdded: { one: 'La til 1 navn.', other: 'La til {n} navn.' },
  namesSkipped: { one: '1 navn var der fra før.', other: '{n} navn var der fra før.' },
  copyReminder: 'Kopier påminnelse',
  reminderText: '{names}: husk å svare på «{name}»! {url}',
  claimQ: 'Er du en av disse?',
  claimHint: 'Trykk på navnet ditt, så lagres svarene dine der. Står du ikke på listen, skriver du bare navnet ditt.',
  claimWho: 'Hvem er du?',
  claimListHint: 'Bare navnene på listen kan svare. Skriv for å finne ditt.',
  claimAdmin: 'Svarer du for en av disse?',
  claimAdminHint: 'Trykk på et navn for å legge svarene der, eller skriv et nytt navn.',
  answerFor: 'Svar for {name}',
  removeName: 'Fjern {name}',
  removeNameQ: 'Fjerne {name} fra listen?',
  remove: 'Fjern',
  showAnswered: 'Vis som besvart',
  draftNoteOwn: 'Lagres som utkast: du står under «Har ikke svart ennå», og svarene telles ikke med før du lagrer med denne på.',
  draftNote: 'Lagres som utkast: personen står under «Har ikke svart ennå», og svarene telles ikke med før de lagres med denne på.',
  draftSaved: 'Utkastet er lagret. Det telles ikke med før det lagres som besvart.',
  savedDraftTitle: 'Utkastet ditt er lagret, {name}.',
  youHaveDraft: 'Du har et utkast som ikke telles med ennå.',
  claimNone: 'Ingen på listen heter det, så det blir et nytt navn.',
  claimMatches: { one: '1 navn på listen passer', other: '{n} navn på listen passer' },
  showAllNames: 'Vis alle ({n})',
  answerAs: 'Svar som {name}',
  copyPersonLink: 'Lenken til {name}',
  deleteEntry: 'Slett svaret til {name}',
  expiresNote: 'Avstemningen slettes automatisk {date}.',
  adminMenu: 'Admin',
  newAdminLink: 'Ny admin-lenke',
  newAdminLinkHint: 'Den gamle slutter å virke',
  newAdminLinkQ: 'Lage en ny admin-lenke? Den gamle slutter å virke med en gang, for alle som har den.',
  makeNewLink: 'Lag ny lenke',
  newLinkDone: 'Ny lenke',
  newAdminLinkDoneText: 'Dette er den nye admin-lenken. Den gamle virker ikke lenger, så send denne til eventuelle andre arrangører.',
  deleteEvent: 'Slett avstemningen',
  deleteEventHint: 'Sletter alt, også alle svarene',
  deleteEventQ: 'Slette avstemningen og alle svarene? Det kan ikke angres.',
  eventDeleted: 'Avstemningen er slettet',
  meMenu: 'Svarene dine',
  newMyLink: 'Ny personlig lenke',
  newMyLinkHint: 'Den gamle slutter å virke',
  newMyLinkQ: 'Lage en ny personlig lenke? Den gamle slutter å virke med en gang.',
  newMyLinkDoneText: 'Dette er din nye personlige lenke. Den gamle virker ikke lenger.',
  deleteMine: 'Slett svarene mine',
  deleteMineQ: 'Slette svarene dine?',
  deleteEntryQ: 'Slette svaret til {name}?',
  personLinkTitle: 'Lenken til {name}',
  personLinkText: 'Den som har denne lenken, kan endre svarene. Send den bare til personen det gjelder.',
  addedByAdminText: 'Svaret er lagt til. Med denne lenken kan personen endre svarene selv senere.',
  closedToast: 'Avstemningen er stengt',
  reopenedToast: 'Avstemningen er åpnet igjen',

  editAnswersOf: 'Endre svarene til {name}',
  yourName: 'Navnet ditt',
  emailHint: 'Lagres med svaret, så du kan finne avstemningen igjen på en annen enhet (vi sender deg en kode). Ingen andre ser den.',
  commentOptional: 'Kommentar (valgfritt)',
  commentPh: 'For eksempel «Kan bare etter kl. 18»',
  whichDays: 'Hvilke dager passer?',
  whichTimes: 'Hvilke tider passer?',
  answerDatesHint: 'Trykk på dagene du kan.',
  answerDatesHintMaybe: 'Trykk én gang for «ja», to ganger for «kanskje» og tre ganger for å fjerne.',
  answerTimesHint: 'Dra over tidene du kan. Dra fra en markert rute for å fjerne. Trykk på en dag øverst for å svare for hele dagen.',
  answerTimesHintMaybe: 'Velg «Kan» eller «Kanskje» og dra over tidene. Dra fra en markert rute for å fjerne. Trykk på en dag øverst for å svare for hele dagen.',
  answerTimesHintTouch: 'Trykk på tidene du kan, eller hold fingeren på en rute og dra over flere. Start på en markert rute for å fjerne. Trykk på en dag øverst for å svare for hele dagen.',
  answerTimesHintTouchMaybe: 'Velg «Kan» eller «Kanskje» og trykk på tidene, eller hold fingeren på en rute og dra over flere. Start på en markert rute for å fjerne. Trykk på en dag øverst for å svare for hele dagen.',
  brush: 'Marker som',
  yesToAll: 'Ja til alle',
  clearAll: 'Nullstill',
  dayYes: 'Kan hele dagen',
  dayMaybe: 'Kanskje hele dagen',
  dayNone: 'Kan ikke denne dagen',
  pickSumDates: 'Du kan {n} av {m} dager',
  pickSumTimes: 'Du kan {dur}',
  pickSumNone: 'Ingenting markert ennå',
  discardQ: 'Forkaste endringene?',
  discard: 'Forkast',

  weeks: 'Uker',
  weekN: 'Uke {n}',
  weekRange: 'Uke {a}–{b}',
  weekPair: 'Uke {a} og {b}',
  gridPaintLabel: 'Tider. Piltastene flytter, mellomrom markerer.',
  gridViewLabel: 'Oversikt over tidene',

  'err.network': 'Fikk ikke kontakt med serveren. Sjekk nettet og prøv igjen.',
  'err.rate': 'For mange forespørsler. Vent litt og prøv igjen.',
  'err.notFound': 'Fant ikke avstemningen.',
  'err.forbidden': 'Du har ikke lov til å gjøre dette.',
  'err.listOnly': 'Bare navnene på listen kan svare. Velg ditt.',
  'err.rename': 'Navnet kan ikke endres i denne avstemningen.',
  'err.decided': 'Det er ikke et av alternativene.',
  'err.closed': 'Avstemningen er stengt for nye svar.',
  'err.full': 'Avstemningen har fått så mange svar som den kan ta.',
  'err.person': 'Fant ikke svaret. Det kan være slettet.',
  'err.personName': 'Skriv inn navnet ditt.',
  'err.email': 'E-postadressen ser ikke riktig ut.',
  'err.emails': 'Høyst 10 e-postadresser.',
  'err.code': 'Koden er feil eller utløpt.',
  'err.link': 'Lenken er allerede brukt eller har gått ut. Be om en ny kode.',
  'err.wait.minute': 'Du ba nettopp om en kode. Vent et minutt før du ber om en ny.',
  'err.wait.hour': 'Du har bedt om flere koder. Prøv igjen om en time.',
  'err.wait.day': 'Grensen for koder i dag er nådd. Prøv igjen i morgen.',
  'err.mail': 'Vi kan ikke sende e-post akkurat nå. Prøv igjen senere.',
  'err.name': 'Gi avstemningen et navn.',
  'err.range': 'Ugyldige datoer.',
  'err.rangeLong': 'Det kan være høyst ett år fra første til siste dag.',
  'err.rangeLongTimes': 'Med klokkeslett kan det være høyst 62 dager fra første til siste dag.',
  'err.hours': 'Velg et gyldig tidsrom.',
  'err.options': 'Velg minst én mulig dag eller tid.',
  'err.tooMany': 'For mange mulige tider (høyst 2000). Velg færre dager eller lengre ruter.',
  'err.server': 'Noe gikk galt. Prøv igjen.',
  'err.taken': 'Noen har nettopp svart under det navnet. Velg et annet, eller skriv navnet ditt.',
  'err.answered': 'Personen har nettopp svart selv, så svarene deres er beholdt.',
  'err.names': 'Skriv minst ett navn.',
  'err.tooManyNames': 'Det blir for mange. En avstemning kan ha høyst 200 navn og svar til sammen.',

  'holiday.newYear': '1. nyttårsdag',
  'holiday.maundyThursday': 'Skjærtorsdag',
  'holiday.goodFriday': 'Langfredag',
  'holiday.easter': '1. påskedag',
  'holiday.easterMonday': '2. påskedag',
  'holiday.mayDay': 'Arbeidernes dag',
  'holiday.constitutionDay': 'Grunnlovsdagen',
  'holiday.ascension': 'Kristi himmelfartsdag',
  'holiday.whitsun': '1. pinsedag',
  'holiday.whitMonday': '2. pinsedag',
  'holiday.christmas': '1. juledag',
  'holiday.boxingDay': '2. juledag',
  'holiday.christmasEve': 'Julaften',
  'holiday.newYearsEve': 'Nyttårsaften',
  'holiday.epiphany': 'Hellige tre kongers dag',
  'holiday.nationalDaySe': 'Sveriges nasjonaldag',
  'holiday.midsummerEve': 'Midtsommeraften',
  'holiday.midsummerDay': 'Midtsommerdagen',
  'holiday.allSaints': 'Allehelgensdag',
  'holiday.greatPrayerDay': 'Store bededag',
  holidayCal: 'Helligdager i kalenderen',
  holidayCalHint: 'Helligdagene får en prikk, og knappen «Helligdager» velger dem. Julaften og nyttårsaften er med, i Sverige også midtsommeraften.',
  'holidayCal.no': 'Norge',
  'holidayCal.se': 'Sverige',
  'holidayCal.dk': 'Danmark',
  'holidayCal.none': 'Ingen',
} satisfies Record<string, Text>;

export type Key = keyof typeof nb;
export type Lang = 'nb' | 'nn' | 'sv' | 'da' | 'is' | 'en';

// Record<Key, …>: a key missing in English (or extra) doesn't compile.
const en: Record<Key, Text> = {
  appName: 'Whenever',
  tagline: 'find the day that suits most people',
  privacy: 'Privacy',
  sourceCode: 'Source code',
  heroTitle: 'Find the time that suits most people',
  heroLead: 'Make an event, pick the possible days or times and share the link. Everyone marks when they can – no sign-up.',
  newEvent: 'New event',
  yourEvents: 'Your events',
  yourEventsHint: 'Only remembered on this device. The links work on any device.',
  showAllEvents: 'Show all ({n})',
  findLink: 'Find your events from another device',
  findTitle: 'Find your events',
  findLead: 'Enter the email address you gave when you made or answered an event. We’ll send a code to it, and the events are then added to this device.',
  findEmail: 'Email address',
  findSend: 'Send code',
  findSent: 'If the address has been used on an event here, an email with a code is on its way. The code works for 10 minutes.',
  findCode: 'Code from the email',
  findShow: 'Show my events',
  findLinkLead: 'Tap the button to add the events for your email address to this device. The link works once.',
  findAgain: 'Send a new code',
  findOther: 'Use another address',
  findAsked: 'Asked for a new code.',
  findFound: { one: 'Found {n} event. It’s been added to this device.', other: 'Found {n} events. They’ve been added to this device.' },
  findNone: 'No events found for this address.',
  openAdmin: 'Admin',
  openMine: 'My answers',
  open: 'Open',
  tagAdmin: 'Admin',
  tagAnswered: 'Answered',
  forget: 'Forget on this device',
  leave: 'Sign out',
  leaveQ: 'Sign out on this device?',
  leaveTextAdmin: 'The admin link is removed from this device, along with the event in your list here. You need the link to get back, so keep it first.',
  leaveTextMine: 'Your personal link is removed from this device, along with the event in your list here. You need the link to change your answers later, so keep it first.',
  leaveEmail: 'You can also get the link back with a code to your email address.',
  leftToast: 'Signed out on this device',
  step1: 'Pick the possible days',
  step1Text: 'Dates, or times on each day. Drop weekends and holidays with one tap.',
  step2: 'Share the link',
  step2Text: 'One link for everyone. Each person gets their own link to change their answers.',
  step3: 'See what works',
  step3Text: 'The days and times that suit most people come first.',
  loading: 'Loading …',
  pageNotFound: 'Page not found',
  toHome: 'Go to the start page',
  eventNotFound: 'Event not found',
  eventNotFoundText: 'It may have been deleted, or the link is wrong. Events are deleted automatically 30 days after their last date.',
  retry: 'Try again',
  reload: 'Reload',
  newVersion: 'A new version is ready.',
  changedMeanwhile: 'Someone else just changed the event.',
  back: 'Back',
  cancel: 'Cancel',
  ok: 'OK',
  done: 'Done',
  save: 'Save',
  saved: 'Saved',
  delete: 'Delete',
  deleted: 'Deleted',
  copy: 'Copy',
  copied: 'Copied',
  share: 'Share',
  link: 'Link',
  more: 'More',
  yes: 'Yes',
  maybe: 'Maybe',
  no: 'No',
  name: 'Name',
  sum: 'Can',
  you: 'you',
  language: 'Language',

  editEvent: 'Edit event',
  aboutEvent: 'About the event',
  eventName: 'Event name',
  eventNamePh: 'For example “Christmas party”',
  descriptionOptional: 'Description (optional)',
  locationOptional: 'Location (optional)',
  whatToFind: 'What are you looking for?',
  'mode.dates': 'Days',
  'mode.datesHint': 'Which days work?',
  'mode.times': 'Times',
  'mode.timesHint': 'Which times work, day by day?',
  fromHour: 'From',
  toHour: 'To',
  slotLength: 'Slots of',
  meetingLength: 'Meeting length',
  timeZone: 'Time zone',
  timeZoneHint: 'Everyone sees the times in this time zone.',
  possibleDays: 'Possible days',
  theme: 'Theme',
  'theme.auto': 'Automatic',
  'theme.autoHint': 'Light or dark, like your device',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.hacker': 'Hacker',
  rangeHint: 'Drag from one day to another to include every day in between, or click the first and shift-click the last.',
  rangeHintTouch: 'Hold your finger on a day and drag to another to include every day in between.',
  possibleDaysHint: 'Tap the days that could work, or pick several with the buttons. The buttons only change the selection – you can still add or drop single days afterwards.',
  period2w: 'Next 2 weeks',
  period4w: 'Next 4 weeks',
  periodMonth: 'All of {month}',
  periodHint: 'Picks exactly these days (instead of the ones you picked)',
  moreMonths: 'Show next month',
  selNone: 'None',
  undo: 'Undo',
  redo: 'Redo',
  removedDays: { one: 'Removed 1 day', other: 'Removed {n} days' },
  timesCleared: 'Times removed',
  weekdays: 'Weekdays',
  weekends: 'Weekends',
  holidays: 'Holidays',
  weekdaysHint: 'Drop the picked weekdays (Monday–Friday), or pick all of them in the calendar',
  weekendsHint: 'Drop the picked Saturdays and Sundays, or pick all of them in the calendar',
  holidaysHint: 'Drop the picked public holidays, or pick all of them in the calendar',
  weekdayHint: 'Drop the picked {day}s, or pick all of them in the calendar',
  toggleWeek: 'Pick or drop week {n}',
  weekShort: 'Wk',
  nDays: { one: '1 day picked', other: '{n} days picked' },
  nSlots: { one: '1 time slot', other: '{n} time slots' },
  nDaysNoTimes: { one: '1 day without times', other: '{n} days without times' },
  possibleTimes: 'Possible times',
  possibleTimesHint: 'Drag across the slots to mark or clear times. Tap a day at the top for more options.',
  possibleTimesHintTouch: 'Tap a slot to mark or clear it, or hold your finger on a slot and drag across more. Tap a day at the top for more options.',
  'preset.all': 'All day',
  'preset.work': 'Working hours 9–17',
  'preset.evening': 'Evenings 17–22',
  'preset.clear': 'No times',
  pickDaysFirst: 'Pick days in the calendar first.',
  dayAll: 'Pick the whole day',
  dayClear: 'Clear all times on this day',
  dayCopy: 'Use these times on every day',
  dayCopyHint: 'Days you add later get these times too',
  dayRemove: 'Remove the day',
  settings: 'Settings',
  'set.maybe': 'Allow “maybe”',
  'set.maybeHint': 'People can answer “if need be”.',
  'set.maybeCounts': 'Count “maybe” as able to come',
  'set.maybeCountsHint': 'Rank by everyone who can or maybe can. Otherwise only yes counts, and on a tie the one with more “maybe” comes first.',
  'set.openEdit': 'change and delete everyone’s answers',
  'set.openEditHint': 'Otherwise only their own.',
  'set.hidden': 'see the answers',
  'set.hiddenHint': 'Who answered what, and how many. Otherwise only the organisers see that, and each participant their own answers.',
  'set.closed': 'Closed for new answers',
  'set.closedHint': 'Nobody but the organisers can answer or change anything.',
  'set.newNames': 'answer under a new name',
  'set.newNamesHint': 'Otherwise only the names you put on the list can answer.',
  'set.claims': 'answer as a name on the list',
  'set.claimsHint': 'By tapping their name with the event link. Otherwise they need their personal link from you.',
  'set.rename': 'change their name',
  'set.renameHint': 'Otherwise a name stays as first written, or as you wrote it on the list.',
  'set.decide': 'pick what it will be',
  'set.decideHint': 'Pick the final day or time, and undo it. The event stays open: only the organisers close it.',
  'set.details': 'change the name, place and description',
  'set.detailsHint': 'Of the event. It doesn’t show who changed them, but you can always change them back.',
  yourEmailOptional: 'Your email (optional)',
  adminEmailsOptional: 'Organisers’ email (optional)',
  addDetails: 'Add a description and place',
  manyWeeks: 'Participants have to go through {n} weeks of times, one week at a time on a phone. Fewer days make it quicker to answer.',
  emailOptional: 'Email (optional)',
  addEmail: 'Add your email',
  adminEmailHint: 'Stored with the event, so you can find the event again on another device (we’ll email you a code). More than one organiser? Add an email each. Only those with the admin link see them.',
  addEmailRow: 'Add another email',
  removeEmail: 'Remove this email',
  createEvent: 'Create event',
  saveChanges: 'Save changes',
  lostAnswers: {
    one: '1 answer is for days or times being removed and will be deleted.',
    other: '{n} answers are for days or times being removed and will be deleted.',
  },
  saveAnyway: 'Save anyway',

  createdTitle: 'Your event is ready!',
  createdText: 'Share the event link with the people you invite. Keep the admin link: it’s how you change the event, and you can share it with other organisers.',
  savedNewTitle: 'Thanks, {name}! Your answers are saved.',
  savedNewText: 'This is your personal link. Use it to change your answers later, from any device.',
  savedNewHint: 'Keep the link: bookmark it or send it to yourself. Don’t share it – whoever has it can change your answers.',
  yourLink: 'Your personal link',
  linkInvalid: 'This link no longer works. It may have been replaced by a new one, or the answers were deleted. You’re seeing the event as a guest.',
  storedAdmin: 'You’re an admin of this event on this device.',
  storedMine: 'This device has your personal link to this event.',
  closedInfo: 'This event is closed for new answers.',
  closedAdmin: 'This event is closed. Only the organisers can add or change answers.',
  metaDates: { one: '1 possible day', other: '{n} possible days' },
  metaTimes: 'Times · the meeting lasts {dur}',
  timesIn: 'Times in {tz}',
  yourTz: '(you’re in {tz})',
  metaHidden: 'Only the organisers see the answers',
  metaOpenEdit: 'Everyone can edit the answers',
  permsTitle: 'Participants can',
  metaListOnly: 'Only the names on the list can answer',
  metaDecide: 'Everyone can pick what it will be',
  editDetails: 'Change name and place',
  editDetailsHint: 'Everyone with the event link sees the change.',
  detailsSaved: 'Changes saved.',
  listOnlyInfo: 'Only the people on the list can answer, with the personal link the organisers gave them.',
  listOnlyClaim: 'Only the names on the list can answer.',
  nameFixed: 'The name can’t be changed in this event.',
  youAreAdmin: 'You’re an admin',
  adminHint: 'You can change the event and every answer.',
  addAnswers: 'Add answers',
  personName: 'Name',
  close: 'Close',
  reopen: 'Reopen',
  hiName: 'Hi, {name}!',
  personalHint: 'This is your personal link. Use it when you want to change your answers.',
  editYourAnswers: 'Change your answers',
  copyMyLink: 'Copy my link',
  shareEvent: 'Share the event',
  addYourAnswers: 'Add your answers',
  shareTitle: 'Share the event',
  shareHint: 'Send this link to everyone who’s invited.',
  eventLink: 'Event link',
  adminLinkTitle: 'Admin link',
  adminLinkHint: 'Share it with anyone who should organise with you. Whoever has it can change the event and every answer.',
  qrTitle: 'QR code for the event',
  resultsTitle: 'What works best?',
  noAnswersYet: 'Nobody has answered yet.',
  noneAvailable: 'None of the options work for anyone yet.',
  hiddenInfo: 'The answers are hidden: only the organisers see them, and how many have answered.',
  'tab.cal': 'Calendar',
  'tab.list': 'List',
  'tab.table': 'Table',
  'tab.grid': 'Grid',
  legendFew: 'Few can',
  legendMany: 'Many can',
  legendTop: 'Most can',
  tapForDetails: 'Tap an option to see who can come.',
  tapCellForDetails: 'Tap a slot to see who can come.',
  nOfM: '{n} of {m} can',
  decidedTitle: 'Decided',
  decidedTag: 'Picked',
  decidedThis: 'This is the pick',
  decideThis: 'Pick {when}',
  decideQ: 'Pick {when}? Everyone who opens the event sees it, and the event closes for new answers.',
  decideOk: 'Pick',
  decidedToast: 'Picked. Share the link and everyone sees it.',
  undecideQ: 'Undo the pick? The event stays closed until you reopen it.',
  decideQOpen: 'Pick {when}? Everyone who opens the event sees it.',
  undecideQOpen: 'Undo the pick?',
  undoDecision: 'Undo the pick',
  undecidedToast: 'The pick is undone',
  addToCalendar: 'Add to calendar',
  nMaybe: { one: '1 maybe', other: '{n} maybe' },
  nMaybeIncl: { one: 'incl. 1 maybe', other: 'incl. {n} maybe' },
  moreTied: { one: 'Show 1 more with as many able to come', other: 'Show {n} more with as many able to come' },
  showFewer: 'Show fewer',
  rankYesFirst: 'Ranked by most yes. On a tie, the one with more “maybe” comes first.',
  rankMaybeCounts: 'Ranked by most who can come, counting “maybe”. On a tie, the one with more yes comes first.',
  canCome: 'Can',
  ifNeedBe: 'Maybe',
  cannot: 'Can’t',
  responses: 'Answers',
  hiddenPeople: 'The answers are hidden. You only see your own.',
  sumYes: 'Can do {n} of {m}',
  sumYesTime: 'Can do {dur}',
  maybeDur: 'maybe {dur}',
  sumNoneDates: 'None of the days work',
  sumNoneTimes: 'None of the times work',
  nOfTotal: '{n} of {m}',
  answeredOf: '{n} of {m} have answered',
  notAnswered: 'Not answered yet',
  noAnswer: 'No answer',
  youNotAnswered: 'You haven’t answered yet.',
  addNames: 'Add names',
  addNamesTitle: 'Who’s invited?',
  addNamesText: 'One name per line, or paste a list. Then you see who hasn’t answered yet, and each gets their own link you can send them. Everyone who opens the event sees the names, unless the answers are hidden.',
  addNamesPh: 'Alex\nSam\nJo',
  addNamesOk: 'Add',
  namesAdded: { one: 'Added 1 name.', other: 'Added {n} names.' },
  namesSkipped: { one: '1 name was already there.', other: '{n} names were already there.' },
  copyReminder: 'Copy reminder',
  reminderText: '{names}: don’t forget to answer “{name}”! {url}',
  claimQ: 'Are you one of these?',
  claimHint: 'Tap your name and your answers go there. Not on the list? Just type your name.',
  claimWho: 'Who are you?',
  claimListHint: 'Only the names on the list can answer. Type to find yours.',
  claimAdmin: 'Answering for one of these?',
  claimAdminHint: 'Tap a name to put the answers there, or type a new name.',
  answerFor: 'Answer for {name}',
  removeName: 'Remove {name}',
  removeNameQ: 'Remove {name} from the list?',
  remove: 'Remove',
  showAnswered: 'Show as answered',
  draftNoteOwn: 'Saved as a draft: you stay under “Not answered yet”, and your answers aren’t counted until you save with this ticked.',
  draftNote: 'Saved as a draft: they stay under “Not answered yet”, and the answers aren’t counted until saved with this ticked.',
  draftSaved: 'Draft saved. It isn’t counted until it’s saved as answered.',
  savedDraftTitle: 'Your draft is saved, {name}.',
  youHaveDraft: 'You have a draft that isn’t counted yet.',
  claimNone: 'Nobody on the list has that name, so it’s added as a new one.',
  claimMatches: { one: '1 name on the list matches', other: '{n} names on the list match' },
  showAllNames: 'Show all ({n})',
  answerAs: 'Answer as {name}',
  copyPersonLink: 'Link for {name}',
  deleteEntry: 'Delete the answers from {name}',
  expiresNote: 'This event is deleted automatically on {date}.',
  adminMenu: 'Admin',
  newAdminLink: 'New admin link',
  newAdminLinkHint: 'The old one stops working',
  newAdminLinkQ: 'Make a new admin link? The old one stops working right away, for everyone who has it.',
  makeNewLink: 'Make a new link',
  newLinkDone: 'New link',
  newAdminLinkDoneText: 'This is the new admin link. The old one no longer works, so send this one to any other organisers.',
  deleteEvent: 'Delete the event',
  deleteEventHint: 'Deletes everything, including all answers',
  deleteEventQ: 'Delete the event and every answer? This can’t be undone.',
  eventDeleted: 'The event was deleted',
  meMenu: 'Your answers',
  newMyLink: 'New personal link',
  newMyLinkHint: 'The old one stops working',
  newMyLinkQ: 'Make a new personal link? The old one stops working right away.',
  newMyLinkDoneText: 'This is your new personal link. The old one no longer works.',
  deleteMine: 'Delete my answers',
  deleteMineQ: 'Delete your answers?',
  deleteEntryQ: 'Delete the answers from {name}?',
  personLinkTitle: 'Link for {name}',
  personLinkText: 'Whoever has this link can change these answers. Only send it to the person it belongs to.',
  addedByAdminText: 'The answers were added. With this link the person can change them later.',
  closedToast: 'The event is closed',
  reopenedToast: 'The event is open again',

  editAnswersOf: 'Change the answers from {name}',
  yourName: 'Your name',
  emailHint: 'Stored with your answers, so you can find the event again on another device (we’ll email you a code). Nobody else sees it.',
  commentOptional: 'Comment (optional)',
  commentPh: 'For example “Only after 6 pm”',
  whichDays: 'Which days work for you?',
  whichTimes: 'Which times work for you?',
  answerDatesHint: 'Tap the days you can make.',
  answerDatesHintMaybe: 'Tap once for “yes”, twice for “maybe” and three times to clear.',
  answerTimesHint: 'Drag across the times you can make. Start on a marked slot to clear. Tap a day at the top to answer for the whole day.',
  answerTimesHintMaybe: 'Choose “Can” or “Maybe” and drag across the times. Start on a marked slot to clear. Tap a day at the top to answer for the whole day.',
  answerTimesHintTouch: 'Tap the times you can make, or hold your finger on a slot and drag across more. Start on a marked slot to clear. Tap a day at the top to answer for the whole day.',
  answerTimesHintTouchMaybe: 'Choose “Can” or “Maybe” and tap the times, or hold your finger on a slot and drag across more. Start on a marked slot to clear. Tap a day at the top to answer for the whole day.',
  brush: 'Mark as',
  yesToAll: 'Yes to all',
  clearAll: 'Clear',
  dayYes: 'Can all day',
  dayMaybe: 'Maybe all day',
  dayNone: 'Can’t this day',
  pickSumDates: 'You can do {n} of {m} days',
  pickSumTimes: 'You can do {dur}',
  pickSumNone: 'Nothing marked yet',
  discardQ: 'Discard your changes?',
  discard: 'Discard',

  weeks: 'Weeks',
  weekN: 'Week {n}',
  weekRange: 'Weeks {a}–{b}',
  weekPair: 'Weeks {a} and {b}',
  gridPaintLabel: 'Times. Arrow keys move, space marks.',
  gridViewLabel: 'Overview of the times',

  'err.network': 'Couldn’t reach the server. Check your connection and try again.',
  'err.rate': 'Too many requests. Wait a moment and try again.',
  'err.notFound': 'Event not found.',
  'err.forbidden': 'You’re not allowed to do that.',
  'err.listOnly': 'Only the names on the list can answer. Pick yours.',
  'err.rename': 'The name can’t be changed in this event.',
  'err.decided': 'That isn’t one of the options.',
  'err.closed': 'This event is closed for new answers.',
  'err.full': 'This event has as many answers as it can take.',
  'err.person': 'Answer not found. It may have been deleted.',
  'err.personName': 'Enter your name.',
  'err.email': 'That email address doesn’t look right.',
  'err.emails': 'At most 10 email addresses.',
  'err.code': 'The code is wrong or has expired.',
  'err.link': 'The link has been used or has expired. Ask for a new code.',
  'err.wait.minute': 'You just asked for a code. Wait a minute before asking for a new one.',
  'err.wait.hour': 'You’ve asked for several codes. Try again in an hour.',
  'err.wait.day': 'Today’s limit for codes is reached. Try again tomorrow.',
  'err.mail': 'We can’t send email right now. Try again later.',
  'err.name': 'Give the event a name.',
  'err.range': 'Invalid dates.',
  'err.rangeLong': 'There can be at most a year from the first to the last day.',
  'err.rangeLongTimes': 'With times, there can be at most 62 days from the first to the last day.',
  'err.hours': 'Pick a valid time window.',
  'err.options': 'Pick at least one possible day or time.',
  'err.tooMany': 'Too many possible times (at most 2000). Pick fewer days or longer slots.',
  'err.server': 'Something went wrong. Try again.',
  'err.taken': 'Someone just answered under that name. Pick another, or type your name.',
  'err.answered': 'They’ve just answered themselves, so their answers were kept.',
  'err.names': 'Type at least one name.',
  'err.tooManyNames': 'That’s too many. An event can have at most 200 names and answers together.',

  'holiday.newYear': 'New Year’s Day',
  'holiday.maundyThursday': 'Maundy Thursday',
  'holiday.goodFriday': 'Good Friday',
  'holiday.easter': 'Easter Sunday',
  'holiday.easterMonday': 'Easter Monday',
  'holiday.mayDay': 'Labour Day',
  'holiday.constitutionDay': 'Constitution Day',
  'holiday.ascension': 'Ascension Day',
  'holiday.whitsun': 'Whit Sunday',
  'holiday.whitMonday': 'Whit Monday',
  'holiday.christmas': 'Christmas Day',
  'holiday.boxingDay': 'Boxing Day',
  'holiday.christmasEve': 'Christmas Eve',
  'holiday.newYearsEve': 'New Year’s Eve',
  'holiday.epiphany': 'Epiphany',
  'holiday.nationalDaySe': 'National Day of Sweden',
  'holiday.midsummerEve': 'Midsummer Eve',
  'holiday.midsummerDay': 'Midsummer Day',
  'holiday.allSaints': 'All Saints’ Day',
  'holiday.greatPrayerDay': 'Great Prayer Day',
  holidayCal: 'Public holidays in the calendar',
  holidayCalHint: 'Holidays get a dot, and the “Holidays” button picks them. Christmas Eve and New Year’s Eve are included, in Sweden Midsummer Eve too.',
  'holidayCal.no': 'Norway',
  'holidayCal.se': 'Sweden',
  'holidayCal.dk': 'Denmark',
  'holidayCal.none': 'None',
};

export const STRINGS: Record<Lang, Record<Key, Text>> = { nb, nn, sv, da, is, en };
// The language menu, in the order of each language's own name.
export const LANGS: readonly Lang[] = ['da', 'en', 'is', 'nb', 'nn', 'sv'];
export const LANG_NAMES: Record<Lang, string> = { da: 'Dansk', en: 'English', is: 'Íslenska', nb: 'Norsk bokmål', nn: 'Norsk nynorsk', sv: 'Svenska' };
const LOCALES: Record<Lang, string> = { nb: 'nb-NO', nn: 'nn-NO', sv: 'sv-SE', da: 'da-DK', is: 'is-IS', en: 'en-GB' };
// The calendars' column heads.
const WEEKDAYS: Record<Lang, string[]> = {
  nb: ['ma', 'ti', 'on', 'to', 'fr', 'lø', 'sø'],
  nn: ['må', 'ty', 'on', 'to', 'fr', 'la', 'sø'],
  sv: ['må', 'ti', 'on', 'to', 'fr', 'lö', 'sö'],
  da: ['ma', 'ti', 'on', 'to', 'fr', 'lø', 'sø'],
  is: ['má', 'þr', 'mi', 'fi', 'fö', 'la', 'su'],
  en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
};
// When n takes the singular. Icelandic also uses it for 21, 31, ... (not
// 11); Intl.PluralRules can't say so, as Chromium has no Icelandic data.
const one = (n: number) => n === 1;
const PLURAL_ONE: Record<Lang, (n: number) => boolean> = { nb: one, nn: one, sv: one, da: one, en: one, is: (n) => n % 10 === 1 && n % 100 !== 11 };
const AND: Record<Lang, string> = { nb: 'og', nn: 'og', sv: 'och', da: 'og', is: 'og', en: 'and' };
// Hours and minutes in durations ("1 t 30 min").
const UNITS: Record<Lang, [h: string, min: string]> = { nb: ['t', 'min'], nn: ['t', 'min'], sv: ['tim', 'min'], da: ['t', 'min'], is: ['klst.', 'mín.'], en: ['h', 'min'] };

export const isLang = (l: unknown): l is Lang => (LANGS as readonly unknown[]).includes(l);

let lang: Lang = 'nb';
export const getLang = (): Lang => lang;
export function setLang(l: unknown): void {
  lang = isLang(l) ? l : 'en';
}

export type Vars = Record<string, string | number>;

export function t(key: Key, vars: Vars = {}): string {
  let s: Text = STRINGS[lang][key] ?? STRINGS.en[key] ?? key;
  if (typeof s === 'object') s = PLURAL_ONE[lang](Number(vars.n)) ? s.one : s.other;
  return s.replace(/\{(\w+)\}/g, (m: string, k: string) => (vars[k] !== undefined ? String(vars[k]) : m));
}

// For keys built at run time (error codes from the server and the like):
// unknown ones come back as the key itself.
export const tx = (key: string, vars?: Vars): string => t(key as Key, vars);

// "A og B", "A, B og C".
export const joinAnd = (items: string[]): string => (items.length < 2 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} ${AND[lang]} ${items[items.length - 1]}`);

// The names of the holidays on one date: "Grunnlovsdagen og 2. pinsedag".
export const holidayNames = (keys: readonly string[]): string => joinAnd(keys.map((k) => tx(`holiday.${k}`)));

export const cap = (s: string): string => (s ? s[0].toUpperCase() + s.slice(1) : s);

// Chromium has no Intl data for nynorsk or Icelandic (it formats them as
// bokmål or English), so those two use their own names, in the style of
// bokmål's Intl output: "måndag 2. november", "2.–9. november 2026".
interface DateNames {
  months: string[];
  monthsShort: string[];
  days: string[]; // Monday first
  daysShort: string[];
  daySep: string; // between the weekday and the day
  rangeSep: string; // between the ends of a range across months
}
const OWN_NAMES: Partial<Record<Lang, DateNames>> = {
  nn: {
    months: ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'],
    monthsShort: ['jan.', 'feb.', 'mars', 'apr.', 'mai', 'juni', 'juli', 'aug.', 'sep.', 'okt.', 'nov.', 'des.'],
    days: ['måndag', 'tysdag', 'onsdag', 'torsdag', 'fredag', 'laurdag', 'søndag'],
    daysShort: ['må.', 'ty.', 'on.', 'to.', 'fr.', 'la.', 'sø.'],
    daySep: ' ',
    rangeSep: '–',
  },
  is: {
    months: ['janúar', 'febrúar', 'mars', 'apríl', 'maí', 'júní', 'júlí', 'ágúst', 'september', 'október', 'nóvember', 'desember'],
    monthsShort: ['jan.', 'feb.', 'mar.', 'apr.', 'maí', 'jún.', 'júl.', 'ágú.', 'sep.', 'okt.', 'nóv.', 'des.'],
    days: ['mánudagur', 'þriðjudagur', 'miðvikudagur', 'fimmtudagur', 'föstudagur', 'laugardagur', 'sunnudagur'],
    daysShort: ['mán.', 'þri.', 'mið.', 'fim.', 'fös.', 'lau.', 'sun.'],
    daySep: ', ',
    rangeSep: '\u2009–\u2009', // thin spaces, as ICU has them
  },
};
const ymd = (d: string) => {
  const [y, m, day] = d.split('-').map(Number);
  return { y, m: m - 1, day };
};

const fmt = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(LOCALES[lang], { timeZone: 'UTC', ...opts });

export type DateStyle = 'long' | 'medium' | 'full' | 'monthShort';
const STYLES: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  long: { weekday: 'long', day: 'numeric', month: 'long' },
  medium: { weekday: 'short', day: 'numeric', month: 'short' },
  full: { day: 'numeric', month: 'long', year: 'numeric' },
  monthShort: { month: 'short' },
};

export function fmtDate(d: string, style: DateStyle = 'long'): string {
  const own = OWN_NAMES[lang];
  if (!own) return fmt(STYLES[style] ?? STYLES.long).format(toTime(d));
  const { y, m, day } = ymd(d);
  if (style === 'full') return `${day}. ${own.months[m]} ${y}`;
  if (style === 'monthShort') return own.monthsShort[m];
  if (style === 'medium') return `${own.daysShort[weekday(d)]}${own.daySep}${day}. ${own.monthsShort[m]}`;
  return `${own.days[weekday(d)]}${own.daySep}${day}. ${own.months[m]}`;
}

export function fmtRange(start: string, end: string): string {
  const own = OWN_NAMES[lang];
  if (own) {
    const a = ymd(start);
    const b = ymd(end);
    if (start === end) return fmtDate(start, 'full');
    if (a.y === b.y && a.m === b.m) return `${a.day}.–${b.day}. ${own.months[a.m]} ${a.y}`;
    if (a.y === b.y) return `${a.day}. ${own.months[a.m]}${own.rangeSep}${b.day}. ${own.months[b.m]} ${a.y}`;
    return `${fmtDate(start, 'full')}${own.rangeSep}${fmtDate(end, 'full')}`;
  }
  const f = fmt(STYLES.full);
  if (start === end) return f.format(toTime(start));
  try {
    return f.formatRange(toTime(start), toTime(end));
  } catch {
    return `${f.format(toTime(start))} – ${f.format(toTime(end))}`;
  }
}

// "november" (nb) / "November" (en).
export function monthName(d: string): string {
  const own = OWN_NAMES[lang];
  return own ? own.months[ymd(d).m] : fmt({ month: 'long' }).format(toTime(d));
}

export function monthTitle(y: number, m: number): string {
  const own = OWN_NAMES[lang];
  return cap(own ? `${own.months[m]} ${y}` : fmt({ month: 'long', year: 'numeric' }).format(Date.UTC(y, m, 1)));
}

export const weekdayShort = (): string[] => WEEKDAYS[lang];

export function weekdayLong(): string[] {
  const own = OWN_NAMES[lang];
  if (own) return [...own.days];
  const f = fmt({ weekday: 'long' });
  // 2024-01-01 was a Monday.
  return Array.from({ length: 7 }, (_, i) => f.format(Date.UTC(2024, 0, 1 + i)));
}

// "2.11." or "2/11".
export function shortDate(d: string): string {
  const { m, day } = ymd(d);
  return lang === 'en' || lang === 'sv' ? `${day}/${m + 1}` : `${day}.${m + 1}.`;
}

export function durationLabel(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const [hu, mu] = UNITS[lang];
  if (!h) return `${m} ${mu}`;
  return m ? `${h} ${hu} ${m} ${mu}` : `${h} ${hu}`;
}

export function slotLabel(key: string, slot: number): string {
  const p = parseKey(key);
  if (!p || p.min === null) return key;
  return `${fmtDate(p.date, 'long')}, ${hhmm(p.min)}–${hhmm(p.min + slot)}`;
}

export function windowLabel(w: Pick<TimeWindow, 'date' | 'start' | 'end'>): string {
  return `${fmtDate(w.date, 'long')}, ${hhmm(w.start)}–${hhmm(w.end)}`;
}

